import React, { useRef, useState, useEffect, useCallback } from 'react'
import dayjs from 'dayjs'
import duration, { Duration, DurationUnitType } from 'dayjs/plugin/duration'
import { Flex, Text, Skeleton, FlexProps } from 'vvs-uikit'
import zip from 'lodash/zip'
import styled from 'styled-components'
import { space, SpaceProps } from 'styled-system'

const units: DurationUnitType[] = ['year', 'month', 'day', 'hour', 'minute', 'second']
const unitsAbbr: string[] = ['yr', 'mo', 'd', 'h', 'm', 's']
const formats: string[] = ['Y', 'M', 'D', 'HH', 'mm', 'ss']

// Helpers
export const isBeforeNow = (timestamp: string): boolean => dayjs(timestamp).isBefore(dayjs())
export const getTimeDiff = (timestamp: string): Duration => {
  dayjs.extend(duration)
  const now = dayjs()
  let future = dayjs(timestamp)
  if (!future.isValid() || isBeforeNow(timestamp)) future = now
  return dayjs.duration(future.diff(now))
}

// Hooks
export const useCountdown = (countTo: string) => {
  const [timeExcerpt, setTimeExcerpt] = useState([])
  const timer = useRef<ReturnType<typeof setTimeout>>(null)

  const excerptTime = useCallback(() => {
    const timeDiff = getTimeDiff(countTo)
    const timeDiffValues = units.map((unit) => timeDiff.get(unit))

    const valueFormatTuples = zip(timeDiffValues, unitsAbbr, formats)
    return valueFormatTuples
      .filter(([value], index, arr) => value !== 0 || index > arr.length - 4)
      .splice(0, 3) // Get Top 3 Significant Time (i.e. 0y 1m 1d 5h 10m 20s --> 1m 1d 5h)
      .map(([_, unit, format]) => [timeDiff.format(format), unit])
  }, [countTo])

  const startTimer = useCallback(() => {
    timer.current = setInterval(() => setTimeExcerpt(excerptTime()), 1000)
  }, [timer, excerptTime])

  useEffect(() => {
    startTimer()
    return () => {
      clearInterval(timer.current)
    }
  }, [timer, startTimer])

  return { timeExcerpt }
}

// Types & Components
interface ContainerProps extends FlexProps, SpaceProps {}
export interface StartFromProps extends ContainerProps {
  startFrom
}
const Container = styled(Flex)<ContainerProps>`
  align-items: center;
  ${space}
`

const TimePiece = ({ value = '', unit = '' }) => {
  return (
    <Flex alignItems="end">
      {React.Children.toArray(
        Array.from(value).map((char) => (
          <Text width="8px" textAlign="center" marginRight="1px">
            {char}
          </Text>
        )),
      )}
      <Text fontSize="14px">{unit}</Text>
    </Flex>
  )
}

const Countdown: React.FC<StartFromProps> = ({ startFrom, ...props }) => {
  const { timeExcerpt } = useCountdown(startFrom)
  return (
    <Container {...props}>
      {/* {timeExcerpt.length > 0 ? <Text whiteSpace="nowrap"> {timeExcerpt} </Text> : <Skeleton width={60} />} */}
      <Flex flexWrap="nowrap">
        {timeExcerpt.length > 0 ? (
          timeExcerpt.map(([value, unit], index, arr) => {
            const isLastPiece = index === arr.length - 1
            return (
              <>
                <TimePiece value={value.toString()} unit={unit} />
                {isLastPiece || (
                  <Text marginLeft="2px" marginRight="1px">
                    :
                  </Text>
                )}
              </>
            )
          })
        ) : (
          <Skeleton width={60} />
        )}
      </Flex>
    </Container>
  )
}
export default Countdown
